import { Injectable } from '@angular/core';
import { forkJoin } from 'rxjs';
import { tap } from 'rxjs/operators';
// Store
import { Selector, State, StateContext, Action, Store } from '@ngxs/store';
// State
import { QualityOrderDetailState } from './quality-order-detail.state';
import { PhotoReviewState } from '../photo-review/state/photo-review.state';
import { RulesState } from '../rules/state/rules.state';
import { PDFAttachmentsState } from '../pdf-review/state/pdf-attachments.state';
import { VirtualTourState } from '../virtual-tour/state/virtual-tour.state';
// Actions
import {
	SetOrderDocument,
	SetOrderDocumentManually,
	SetSchemaDocument,
	SetOrderDetailModel,
	SetStateSelectedOrder,
	GetOrderRevisedFields,
	CleanQualityOrder,
	UnregisterFromOrderChannel,
	SetOrderisRecalled,
	SetSelectedSection,
	SetOrderPublicRecord,
	SetLeftMenu, ClearLeftMenu
} from './actions/quality.actions';
import { ClearRules } from '../rules/state/action/rules.actions';
import { GENERAL_EVENT_TYPE, Logout, SignalRService } from '@cv-invision/core';
// Models
import { OrderCI_STATE_PATH, QualityStateModel } from './model/quality-state.model';
import { RevisedFieldModel } from '../model/revised-fields.model';
import { OrderDetailModel } from './model/order-detail.model';
import { PublicRecordModel } from '@cv-invision/cv.invision-views.ux';
// Service
import { QualityDocumentService } from '../service/document-quality.service';
// tslint:disable-next-line: nx-enforce-module-boundaries
import { NavUtils } from '../../../../../../apps/qc/src/app/models/nav.utils';

@State<QualityStateModel>({
	name: OrderCI_STATE_PATH,
	defaults: new QualityStateModel(),
	children: [
		QualityOrderDetailState,
		PhotoReviewState,
		RulesState,
		PDFAttachmentsState,
		VirtualTourState
	]
})
@Injectable()
export class QualityState {
	constructor(
		private store: Store,
		private qualityService: QualityDocumentService,
		private signalRService: SignalRService
	) {}

	/**
	 * Get Feature Quality State Model.
	 * @param state: QualityStateModel
	 */
	@Selector()
	static getQuality(state: QualityStateModel): QualityStateModel {
		return state;
	}

	/**
	 * Get Feature Order Detail State Model.
	 * @param state: QualityStateModel
	 */
	@Selector()
	static getOrderDetail(state: QualityStateModel): OrderDetailModel {
		return state.orderDetail;
	}

	/**
	 * Get Current Document Order.
	 * @param state: QualityStateModel
	 */
	@Selector()
	static getOrderDocument(state: QualityStateModel): QualityStateModel {
		return state.documentOrder;
	}

	/**
	 * Get Current Document Schema.
	 * @param state: QualityStateModel
	 */
	@Selector()
	static geSchemaDocument(state: QualityStateModel): QualityStateModel {
		return state.documentSchema;
	}

	/**
	 * Get revised field Order.
	 * @param state: QualityStateModel
	 */
	@Selector()
	static getRevisedFields(state: QualityStateModel): RevisedFieldModel {
		return state.revisedFields;
	}

	/**
	 * Get whether an order is being recalled.
	 * @param state: QualityStateModel
	 */
	@Selector()
	static getIsbeingRecalled(state: QualityStateModel): boolean {
		return state.isRecalled;
	}

	/**
	 * Get the last selected section
	 * @param state: QualityStateModel
	 */
	@Selector()
	static getSelectedSection(state: QualityStateModel): QualityStateModel {
		return state.selectedSection;
	}

	/**
	 * Get the last selected section
	 * @param state: QualityStateModel
	 */
	@Selector()
	static getPublicRecords(state: QualityStateModel): PublicRecordModel[] {
		return state.publicRecords?.records || [];
	}

	/**
	 * Save the current selected Row Order
	 * @param ctx
	 * @param payload
	 */
	@Action(SetOrderDocument)
	setSelectedDocumentOrder(
		ctx: StateContext<QualityStateModel>,
		{ orderId }: SetOrderDocument
	): any {
		return this.qualityService.getOrderDocument(orderId).pipe(
			tap(doc => {
				ctx.patchState({
					documentOrder: doc
				});
			})
		);
	}

	/**
	 * Save the current selected section
	 * @param ctx
	 * @param payload
	 */
	@Action(SetSelectedSection)
	setSelectedSection(
		ctx: StateContext<QualityStateModel>,
		{section}: SetSelectedSection
	): any {
		ctx.patchState({
			selectedSection: section
		});
	}

	/**
	 * Save the current selected Row Order
	 * @param ctx
	 * @param payload
	 */
	@Action(SetSchemaDocument)
	setSelectedDocumentSchema(
		ctx: StateContext<QualityStateModel>,
		{ schemaName }: SetSchemaDocument
	): any {
		return this.qualityService.getOrderSchema(schemaName).pipe(
			tap(schema => {
				ctx.patchState({
					documentSchema: schema
				});
			})
		);
	}

	/**
	 * Set the Order Detail
	 * @param ctx
	 * @param payload
	 */
	@Action(SetOrderDetailModel)
	setOrderDetailModel(
		ctx: StateContext<QualityStateModel>,
		{ orderDetailModel }: SetOrderDetailModel
	): any {
		ctx.patchState({
			orderDetail: orderDetailModel
		});
	}

	/**
	 * get revised file for an order that was resubmit
	 * @param ctx
	 * @param orderId
	 */
	@Action(GetOrderRevisedFields)
	setOrderRevisedFields(
		ctx: StateContext<QualityStateModel>,
		{ orderId }: GetOrderRevisedFields
	): any {
		return this.qualityService.getRevisedFields(orderId).pipe(
			tap(revisedFields => {
				ctx.patchState({
					revisedFields
				});
			})
		);
	}

	/**
	 * Save the current selected Row Order
	 * @param ctx
	 * @param payload
	 */
	@Action(SetOrderDocumentManually)
	setSelectedDocumentOrderManually(
		ctx: StateContext<QualityStateModel>,
		{ doc }: SetOrderDocumentManually
	): any {
		ctx.patchState({
			documentOrder: doc
		});
	}

	/**
	 * Save whether an order is being recalled
	 * @param ctx
	 * @param isRecalled
	 */
	@Action(SetOrderisRecalled)
	SetOrderisRecalled(
		ctx: StateContext<QualityStateModel>,
		{ isRecalled }: SetOrderisRecalled
	): any {
		ctx.patchState({
			isRecalled: isRecalled
		});
	}

	/**
	 * change stateEnum when a selected order have been rejected or approved
	 * @param ctx
	 * @param payload
	 */
	@Action(SetStateSelectedOrder)
	setStateSelectedOrder(
		ctx: StateContext<QualityStateModel>,
		{ stateEnum }: SetStateSelectedOrder
	): any {
		ctx.patchState({
			stateEnum: stateEnum
		});
	}

	/**
	 * Set Order Subject & Comps Public Records
	 * @param ctx
	 * @param payload
	 */
	@Action(SetOrderPublicRecord)
	setOrderPublicRecord(ctx: StateContext<QualityStateModel>, { orderId }: SetOrderPublicRecord): any {

		return forkJoin([
			this.qualityService.getOrderSubjectPublicRecord(orderId),
			// this.qualityService.getOrderSubjectPublicRecord(orderId)
			this.qualityService.getOrderCompsPublicRecord(orderId)
		]).pipe(
			tap(([subjectPR, compsPR]) => {
				let records: PublicRecordModel[] = [];

				if (subjectPR.isValid) {
					records = records.concat(subjectPR.key);
				}

				if (compsPR.isValid) {
					records = records.concat(compsPR.key);
				}

				const publicRecords = {
					records
				};

				ctx.patchState({
					publicRecords
				});
			})
		);
	}

	@Action(SetLeftMenu)
	setLeftMenu(ctx: StateContext<QualityStateModel>, { orderDetail }: SetLeftMenu): void {
		let menuItems: any = NavUtils.getSchemaNav(orderDetail.schema, orderDetail.isCMPForm);
		const indexOverview = menuItems.findIndex((n: any) => n.path === 'Overview');
		if (orderDetail.revised) {
			const sch = this.store.selectSnapshot(s => s.user.schema);
			const rvs = sch.filter((s: any) => s.type === 'revised');
			const {navItems: items} = rvs[0];
			menuItems = menuItems.filter((n: any) => !n.isRevised);

			items.forEach((n: any) => {
				menuItems.splice(indexOverview, 0, n);
			});
		}

		const navItems = menuItems.map((n: any) => {
			const {module: m} = n;
			if (n.path === 'helpcenter') {
				return n;
			}
			if (m === 'quality') {
				const route = `${n.basePath}${n.path}/${orderDetail.id}`;
				return {
					...n,
					route
				};
			}
			return n;
		});
		ctx.patchState({
			menu: navItems
		});
	}

	@Action(ClearLeftMenu)
	clearLeftMenu(ctx: StateContext<QualityStateModel>): void {
		ctx.patchState({
			menu: []
		});
	}

	/**
	 * Helps to destroy or unsubscribe any open action on the Signal R for an order
	 * Attach to it any last unregistered event for an order
	 * @param ctx
	 * @constructor
	 */
	@Action(UnregisterFromOrderChannel)
	UnregisterUserFromOrderChannel(ctx: StateContext<QualityStateModel>): void {
		const state = ctx.getState();
		// After moving from the component, unregister the channel if exists the ID
		const orderDetailModel = state.orderDetail;
		if (orderDetailModel && orderDetailModel.id) {
			if (this.signalRService.isOnline()) {
				// invoke the unsubscribe event for the current order
				this.signalRService
					.invokeHubPublic(GENERAL_EVENT_TYPE.UNSUBSCRIBE, `orders:${orderDetailModel.id}`)
					.then(() => {
						// Swallow
					})
					.catch(() => {
						// Swallow
					});
				// this is not calling the current unsubscribe event some odd behavior
				this.signalRService.unregisterChannel(`orders:${orderDetailModel.id}`);
			}
		}
	}

	/**
	 * clean Quality store
	 * @param ctx
	 */
	@Action(CleanQualityOrder)
	CleanQualityOrder(ctx: StateContext<QualityStateModel>): void {
		this.store.dispatch(new ClearRules()).subscribe(() => {
			const reset = new QualityStateModel();
			ctx.setState(reset);
		});
	}

	/**
	 * Logout.
	 * @param ctx: InboxStateModel
	 */
	@Action(Logout)
	logout(ctx: StateContext<QualityStateModel>): void {
		// Before logout, do some cleaning
		this.store.dispatch(new UnregisterFromOrderChannel()).subscribe(() => {
			ctx.setState(new QualityStateModel());
		});
	}
}
